import { FileUploaderDropZone, fileInputAcceptTypes } from '@systemeio/file-manager'
import { FormSelect } from '@systemeio/ui'
import { useCampaignsList } from 'modules/campaigns/hooks/use-campaigns-list'
import { CampaignId } from 'modules/campaigns/types/campaign-interface'
import ContactSelectTags from 'modules/contacts/contact-page/components/contact-select-tags'
import LastContactsImportsTable from 'modules/contacts/contacts-import/components/last-contacts-imports-table'
import { SelectColumnsForContactsImportModal } from 'modules/contacts/contacts-import/components/select-columns-for-contacts-import-modal'
import { ContactsImportPageLoco } from 'modules/contacts/contacts-import/constants/contacts-import-page-loco'
import {
  ContactsImportDelimiter,
  getContactsImportDelimiterSelectOptions,
} from 'modules/contacts/contacts-import/types/contacts-import-delimeter-type'
import { ContactsLastImportsApiInterface } from 'modules/contacts/contacts-import/types/contacts-import-interface'
import { ContactBulkApiInterface } from 'modules/contacts/types/contact-menu-interface'
import { CreateNewTagModal } from 'modules/tags/components/create-new-tag-modal'
import { NextPage } from 'next'
import { useMemo, useRef, useState } from 'react'
import ButtonLikeLink from 'shared/components/button-like-link'
import { InfoBlockOrderedListItem } from 'shared/components/info-block-ordered-list-item'
import { RequestModuleEnum } from 'shared/enums/request-module-enum'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import { useRevalidateModules } from 'shared/hooks/use-revalidate-modules'
import { getBaseServerSideProps } from 'shared/utils/get-base-server-side-props'
import { KeyedMutator } from 'swr'
import { CheckCircleIcon, InfoIcon, QuestionMarkIcon } from '@systemeio/ui/icons'

const ContactEdit: NextPage = () => {
  const { t } = useLocoTranslation()

  const lastContactsImportsTableRef = useRef<HTMLDivElement>(null)
  const mutateLastContactsImportsTable =
    useRef<KeyedMutator<ContactsLastImportsApiInterface> | null>(null)

  const [file, setFile] = useState<File | null>(null)
  const [delimiter, setDelimiter] = useState<ContactsImportDelimiter>(ContactsImportDelimiter.comma)
  const [selectedTags, setSelectedTags] = useState<ContactBulkApiInterface[] | undefined>([])
  const [selectedCampaignId, setSelectedCampaignId] = useState<CampaignId>()

  const { campaignsList } = useCampaignsList()

  const { revalidateModules } = useRevalidateModules()

  const campaignsListOptions = useMemo(
    () => campaignsList?.items.map(({ id, name }) => ({ id, caption: name })),
    [campaignsList],
  )

  const clearStates = () => {
    setSelectedCampaignId(undefined)
    setSelectedTags([])
    setDelimiter(ContactsImportDelimiter.comma)
    setFile(null)
  }

  const setMutateLastContactsImports = (mutate: KeyedMutator<ContactsLastImportsApiInterface>) => {
    mutateLastContactsImportsTable.current = mutate
  }

  const contactsImportDelimiterSelectOptions = useMemo(
    () => getContactsImportDelimiterSelectOptions(t),
    [t],
  )

  return (
    <div className="grid gap-6 lg:gap-11 xl:grid-cols-[1fr_485px]">
      <div
        className="border-danger bg-background-secondary flex flex-col gap-7 rounded-md
        border-l-[5px] p-6"
      >
        <span>{t('dashboard.contact_import.warning_message.text')}</span>
        <ButtonLikeLink
          className="font-bold"
          onClick={() => {
            lastContactsImportsTableRef.current?.scrollIntoView()
          }}
        >
          {t('dashboard.contact_import.warning_message.button')}
        </ButtonLikeLink>
      </div>

      <div
        className={`bg-background-secondary flex h-fit flex-col gap-6
        rounded-md p-6
        xl:col-start-2 xl:row-start-1 xl:row-end-7`}
      >
        <h3 className="mb-4 text-[18px] leading-6">
          <QuestionMarkIcon className={`fill-brand-primary mr-2 inline h-[20px] w-[20px]`} />
          {t('dashboard.contact_import.instruction.title-2')}
        </h3>
        <ul className="flex flex-col gap-6">
          <InfoBlockOrderedListItem
            dangerouslySetInnerHTML={{ __html: t('dashboard.contact_import.instruction.first') }}
          />
          <InfoBlockOrderedListItem
            dangerouslySetInnerHTML={{ __html: t('dashboard.contact_import.instruction.second') }}
          />
          <InfoBlockOrderedListItem
            dangerouslySetInnerHTML={{ __html: t('dashboard.contact_import.instruction.third') }}
          />
          <InfoBlockOrderedListItem
            dangerouslySetInnerHTML={{ __html: t('dashboard.contact_import.instruction.fourth') }}
          />
          <InfoBlockOrderedListItem
            dangerouslySetInnerHTML={{ __html: t('dashboard.contact_import.instruction.fifth') }}
          />
          <ul className={'list-disc pl-14'}>
            <li className={'mb-2'}>
              {t('dashboard.contact_import.instruction.subparagraph_first')}
            </li>
            <li>{t('dashboard.contact_import.instruction.subparagraph_second')}</li>
          </ul>
          <InfoBlockOrderedListItem
            dangerouslySetInnerHTML={{ __html: t('dashboard.contact_import.instruction.sixth') }}
          />
        </ul>
        <span className="text-text-primary text-[16px] font-bold leading-6">
          {t('dashboard.contact_import.instruction_popup.title')}
        </span>
        <ul className="flex flex-col gap-6">
          <InfoBlockOrderedListItem
            dangerouslySetInnerHTML={{
              __html: t('dashboard.contact_import.instruction_popup.first'),
            }}
          />
          <InfoBlockOrderedListItem
            dangerouslySetInnerHTML={{
              __html: t('dashboard.contact_import.instruction_popup.second'),
            }}
          />
          <InfoBlockOrderedListItem
            dangerouslySetInnerHTML={{
              __html: t('dashboard.contact_import.instruction_popup.third-2'),
            }}
          />
        </ul>
        <span className="text-text-primary text-[16px] font-bold leading-6">
          {t('dashboard.contact_import.instruction_email.title')}
        </span>
        <ul className={'list-disc pl-3.5'}>
          <li className={'mb-2'}>{t('dashboard.contact_import.instruction_email.first')}</li>
          <li>{t('dashboard.contact_import.instruction_email.second')}</li>
        </ul>
      </div>

      <div className="bg-background-secondary rounded-md p-6">
        <h1 className={'mb-6 flex gap-2 self-start text-[17px] lg:self-center'}>
          {t('dashboard.contact_import.import_file_block.title')}
          {file && <CheckCircleIcon className={'fill-green h-[20px] w-[20px]'} />}
        </h1>
        <div className="flex flex-wrap gap-7">
          <div className="flex grow basis-[300px] flex-col gap-9">
            <FormSelect
              withoutCloseIcon
              label={t('dashboard.contact_import.form.label.delimiter')}
              data={contactsImportDelimiterSelectOptions}
              value={delimiter}
              onChange={data => data && setDelimiter(data)}
            />
          </div>
          <div className={'flex flex-col gap-3.5'}>
            <FileUploaderDropZone
              acceptTypes={fileInputAcceptTypes.csv}
              className="grow basis-40 md:shrink-0"
              dropZoneClassName="h-full"
              onSelectFile={setFile}
              label={
                <>
                  {file ? (
                    <p>{file.name}</p>
                  ) : (
                    <p>{t('dashboard.contact_import.form.label.import-file.title')}</p>
                  )}
                </>
              }
              toastText={t('dashboard.contact_import.form.toast.file_upload_complete')}
            />
            <p className={'flex items-center'}>
              <InfoIcon className={'fill-brand-primary mr-2 inline'} />
              {t('dashboard.contact_import.form.label.import-file.info')}
            </p>
          </div>
        </div>
      </div>

      <ContactSelectTags
        className={`bg-background-secondary rounded-lg p-6 xl:col-start-1`}
        tempTags={selectedTags}
        setTempTags={setSelectedTags}
        resetTags={() => setSelectedTags([])}
        additionalButtonBarComponent={
          <CreateNewTagModal
            onCreate={async () => {
              await revalidateModules([RequestModuleEnum.Tags])
            }}
          />
        }
      />
      <div className="bg-background-secondary flex flex-wrap gap-8 rounded-md p-6 lg:self-center">
        <div className={'flex flex-1 flex-col'}>
          <div className={'mb-6'}>
            <h1 className={'mb-2 self-start text-[17px] lg:self-center'}>
              {t('dashboard.contact_import.campaign_title')}
            </h1>
            <span>{t('dashboard.contact_import.form.label.campaign')}</span>
          </div>
          <div className="flex flex-wrap gap-7">
            <div className="flex grow basis-[300px] flex-col gap-9">
              <FormSelect
                isPreFetching={!campaignsList}
                data={campaignsListOptions}
                value={selectedCampaignId}
                onChange={setSelectedCampaignId}
              />
            </div>
          </div>
        </div>
        <div className={'sm:max-w-[340px]'}>
          <div className={'border-text-secondary/30 rounded-lg border p-6'}>
            <div className={'mb-10 flex items-center gap-3'}>
              <QuestionMarkIcon className={`fill-brand-primary inline h-[16px] w-[16px]`} />
              <span className={'text-text-primary text-base font-bold'}>
                {t('dashboard.contact_import.campaign_instruction.title')}
              </span>
            </div>
            <div>
              <span
                dangerouslySetInnerHTML={{
                  __html: t('dashboard.contact_import.campaign_instruction.first'),
                }}
              />
              <ul className={'mt-3 list-disc pl-3.5'}>
                <li className={'mb-2'}>
                  {t('dashboard.contact_import.campaign_instruction.second')}
                </li>
                <li>{t('dashboard.contact_import.campaign_instruction.third')}</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
      <div className={'flex justify-center'}>
        <SelectColumnsForContactsImportModal
          file={file}
          delimiter={delimiter}
          campaign={selectedCampaignId}
          tags={selectedTags}
          onConfirm={async () => {
            mutateLastContactsImportsTable.current &&
              (await mutateLastContactsImportsTable.current())
          }}
          clearStates={clearStates}
        />
      </div>
      <LastContactsImportsTable
        ref={lastContactsImportsTableRef}
        className={`rounded-lg lg:col-start-1`}
        setMutateLastContactsImports={setMutateLastContactsImports}
      />
    </div>
  )
}

export const getServerSideProps = getBaseServerSideProps(
  ContactsImportPageLoco,
  'header.top_menu.contacts',
)

export default ContactEdit
